import React from "react"
import { Text, Modal, Button, useModal, Flex } from "@capioweb/ui-components/src"
import { createMarkup } from "../../utils"
import { LocalesContext } from "../../context"

interface AccountFormInfoModalProps {
	id: string
	heading: string
	body: string
	lastFocusedElement?: HTMLElement | any
}

const InfoModal: React.FC<AccountFormInfoModalProps> = (props) => {
	const { translate, translationKeys } = React.useContext(LocalesContext)
	const { closeModal } = useModal()
	const headingId = `heading-${props.id}`

	const returnFocusOnClose = () => {
		if (props.lastFocusedElement) {
			props.lastFocusedElement?.focus()
		}
	}

	const ref = React.useRef<HTMLDivElement>(null)

	const [firstFocableElement, setFirstFocableElement] =
		React.useState<HTMLButtonElement | HTMLAnchorElement>()
	const [lastFocableElement, setLastFocableElement] =
		React.useState<HTMLElement>()

	React.useEffect(() => {
		if (ref.current) {
			const focableElements = Array.from(
				ref.current?.querySelectorAll(
					'button:not([tabindex="-1"]), a:not([tabindex="-1"])'
				)
			)
			setFirstFocableElement(
				focableElements[0] as HTMLButtonElement | HTMLAnchorElement
			)
			setLastFocableElement(
				focableElements[focableElements.length - 1] as
				| HTMLButtonElement
				| HTMLAnchorElement
			)
		}
		firstFocableElement?.focus()
	}, [ref.current])

	React.useEffect(() => {
		if (document) {
			document.addEventListener('keydown', trapFocus)
			return () => {
				document.removeEventListener("keydown", trapFocus)
			}
		}
	}, [ref.current])

	const trapFocus = (e: KeyboardEvent) => {
		if (e.key === 'Escape') {
			closeModal()
			returnFocusOnClose()
		}

		if (e.key !== 'Tab') return

		if (!firstFocableElement || !lastFocableElement) return

		if (e.shiftKey) {
			// Shift + Tab
			if (document.activeElement === firstFocableElement) {
				e.preventDefault()
				lastFocableElement.focus()
			}
		} else {
			// Tab
			if (document.activeElement === lastFocableElement) {
				e.preventDefault()
				firstFocableElement.focus()
			}
		}
	}



	return (
		<Modal.Window
			id={props.id}
			aria-labelledby={headingId}
			closeBtnLabel={translate(translationKeys.close)}
			onClose={returnFocusOnClose}
		>
			<div ref={ref}>
				<Text id={headingId} variant="headline-1">
					{props.heading}
				</Text>
				<Text as="div" variant="paragraph-16" mb={{ _: 3, m: 4 }}>
					<div dangerouslySetInnerHTML={createMarkup(props.body)}></div>
				</Text>
				<Button
					aria-controls={props.id}
					onClick={() => {
						closeModal()
						returnFocusOnClose()
					}}
				>
					{translate(translationKeys.close)}
				</Button>
			</div>
		</Modal.Window>
	)
}

export { InfoModal }
